import { RefetchQueryFilters } from '@tanstack/react-query';
import { DataQueryKey } from '../../../hooks/useDataQuery/dataQuery.types';
import PATHS from '../../../routes/routes.const';
import { OfflineStatus } from '../../offline/domain/offlineOrder/offlineOrder.types';
import { UserRole } from '../../users/roles';
import { ActionEnum } from '../actions/types/orderActions.types';
import { hasSplit } from '../conditions/orderCondition.utils';
import { OrderStatus } from '../interfaces/order-status.interface';
import {
  BrandSplitSettings,
  StatusType,
  SynchronizedType,
  lostOrderStatuses,
} from '../order.types';
import { OrderView } from '../ordersView.types';

export const isActionToFinishOrApproveOrder = (action: ActionEnum) =>
  [
    ActionEnum.FINISH_TO_IN_REVISION,
    ActionEnum.FINISH_TO_ON_APPROVAL,
    ActionEnum.FINISH_TO_APPROVED,
  ].includes(action);

export const isActionToChangeToOrderEditMode = (action: ActionEnum) =>
  [
    ActionEnum.BACK_TO_ON_APPROVAL,
    ActionEnum.BACK_TO_IN_REVISION,
    ActionEnum.BACK_TO_DRAFT,
  ].includes(action);

export const isActionToRemainInOrderFinishedPage = (action: ActionEnum) =>
  [
    ActionEnum.MOVE_TO_CANCELED,
    ActionEnum.FINISH_TO_APPROVED,
    ActionEnum.MOVE_TO_SENT,
    ActionEnum.BACK_TO_APPROVED,
  ].includes(action);

export const isAbandonedOrCanceledOrder = (orderStatus: OrderStatus | undefined) =>
  [StatusType.ABANDONED, StatusType.CANCELED].includes(orderStatus as StatusType);

export const isLostOrder = (orderStatus?: StatusType | OfflineStatus) =>
  lostOrderStatuses.includes(orderStatus as StatusType);

export const isSuccessSynchronized = (synchronizedStatus?: SynchronizedType) => {
  return (
    synchronizedStatus === SynchronizedType.SUCCESS ||
    synchronizedStatus === SynchronizedType.SUCCESS_WITH_PAYMENT_ERROR
  );
};

export const actionsThatChangeStatus: ActionEnum[] = [
  ActionEnum.BACK_TO_APPROVED,
  ActionEnum.BACK_TO_DRAFT,
  ActionEnum.BACK_TO_IN_REVISION,
  ActionEnum.BACK_TO_ON_APPROVAL,
  ActionEnum.FINISH_TO_APPROVED,
  ActionEnum.FINISH_TO_IN_REVISION,
  ActionEnum.FINISH_TO_ON_APPROVAL,
  ActionEnum.MOVE_TO_CANCELED,
  ActionEnum.MOVE_TO_SENT,
];

export const isActionToChangeStatus = (action?: ActionEnum) => {
  if (!action) {
    return false;
  }
  return actionsThatChangeStatus.includes(action);
};

export const isStatusAfterDraft = (orderStatus: StatusType) =>
  [
    StatusType.DRAFT,
    StatusType.IN_REVISION,
    StatusType.ON_APPROVAL,
    StatusType.APPROVED,
    StatusType.SENT,
  ].includes(orderStatus);

export const needToGoBackToList = ({
  orderSplitSettings,
  action,
}: {
  orderSplitSettings?: BrandSplitSettings[];
  action?: ActionEnum;
}) => {
  const orderHasSplit = orderSplitSettings && hasSplit(orderSplitSettings);

  const needToGoBackToOrderListPage =
    orderHasSplit && action && isActionToFinishOrApproveOrder(action);

  return needToGoBackToOrderListPage;
};

export const validateOrderQuery: (teamId?: string, orderId?: string) => RefetchQueryFilters = (
  teamId,
  orderId,
) => ({
  queryKey: [teamId, DataQueryKey.ORDER_VALIDATE, orderId],
  type: 'active',
  exact: true,
});

export const validateOrderGeneralDataQuery: (
  orderId: string,
  teamId?: string,
) => RefetchQueryFilters = (orderId, teamId) => ({
  queryKey: [teamId, DataQueryKey.ORDER_VALIDATE_GENERAL_DATA, orderId],
  type: 'active',
  exact: true,
});

export const validateOrderProductsQuery: (
  orderId?: string,
  teamId?: string,
) => RefetchQueryFilters = (orderId, teamId) => ({
  queryKey: [teamId, DataQueryKey.ORDER_VALIDATE_PRODUCTS, orderId],
  type: 'active',
  exact: true,
});

export const validateOrderConditionsQuery: (
  orderId: string,
  teamId?: string,
) => RefetchQueryFilters = (orderId, teamId) => ({
  queryKey: [teamId, DataQueryKey.ORDER_VALIDATE_CONDITIONS, orderId],
  type: 'active',
  exact: true,
});

export const breakWordsByLetter = (text?: string) => text?.split('').join('\u200B');

const ROLES_WITH_PERMISSION_TO_EDIT_SUGGESTED_ORDER: UserRole[] = ['VENDEDOR', 'ADMIN_MARCA'];

export const canRoleEditSuggestedOrder = (
  role: UserRole | undefined,
  sellerCanCreateOrChangeSuggestedOrder: boolean,
) => {
  if (role === 'VENDEDOR') {
    return sellerCanCreateOrChangeSuggestedOrder;
  }

  return !!role && ROLES_WITH_PERMISSION_TO_EDIT_SUGGESTED_ORDER.includes(role);
};

export const getOrderViewMode = (isPath: (path: string) => boolean): OrderView | undefined => {
  if (isPath(PATHS.main.orders.editForm.path)) {
    return OrderView.EDIT;
  }
  if (isPath(PATHS.main.orders.finished.path)) {
    return OrderView.FINISHED;
  }
  if (isPath(PATHS.main.orders.suggestion.path)) {
    return OrderView.SUGGESTION;
  }
};
